import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card } from '../ui'

const DEFAULT_HIGHLIGHT_PADDING = 6
const VIEWPORT_PADDING = 12
const TOOLTIP_MAX_WIDTH = 340

const clamp = (value, min, max) => {
  if (max < min) {
    return min
  }
  return Math.min(Math.max(value, min), max)
}

const TUTORIAL_STEPS = [
  {
    targetId: 'room-now-playing',
    highlightPadding: 8,
    title: 'Get oriented quickly',
    body: 'This top card shows what is playing right now and how much time remains.'
  },
  {
    targetId: 'queue-section',
    highlightPadding: 8,
    title: 'Follow the queue',
    body: 'Queue and History live here. Drag queued songs to reorder or remove songs as needed.'
  },
  {
    targetId: 'search-launcher',
    highlightPadding: 2,
    title: 'Add songs from the bottom search bar',
    body: 'Tap the bottom search launcher to open search. Search songs or paste a YouTube URL.'
  },
  {
    targetId: 'utility-controls',
    highlightPadding: 2,
    title: 'Use quick controls',
    body: 'Pitch and Emoji are in the bottom utility bar so you can stay in flow.'
  }
]

const FirstTimeTutorialSheet = ({ isOpen = false, onClose, onComplete }) => {
  const [stepIndex, setStepIndex] = useState(0)
  const [spotlightRect, setSpotlightRect] = useState(null)
  const [viewportSize, setViewportSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 390,
    height: typeof window !== 'undefined' ? window.innerHeight : 844
  })
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 })
  const tooltipRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      setStepIndex(0)
    }
  }, [isOpen])

  const step = useMemo(() => TUTORIAL_STEPS[stepIndex], [stepIndex])
  const isLastStep = stepIndex === TUTORIAL_STEPS.length - 1

  const updateSpotlight = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    }

    setViewportSize((previous) => {
      if (previous.width === viewport.width && previous.height === viewport.height) {
        return previous
      }
      return viewport
    })

    const target = document.querySelector(`[data-tutorial-id="${step.targetId}"]`)
    if (!target) {
      setSpotlightRect(null)
      return
    }

    const rect = target.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) {
      setSpotlightRect(null)
      return
    }

    const highlightPadding = Number.isFinite(step.highlightPadding)
      ? step.highlightPadding
      : DEFAULT_HIGHLIGHT_PADDING

    const left = clamp(
      rect.left - highlightPadding,
      VIEWPORT_PADDING,
      viewport.width - VIEWPORT_PADDING
    )
    const top = clamp(
      rect.top - highlightPadding,
      VIEWPORT_PADDING,
      viewport.height - VIEWPORT_PADDING
    )
    const right = clamp(
      rect.right + highlightPadding,
      left + 1,
      viewport.width - VIEWPORT_PADDING
    )
    const bottom = clamp(
      rect.bottom + highlightPadding,
      top + 1,
      viewport.height - VIEWPORT_PADDING
    )

    const nextRect = {
      left,
      top,
      width: Math.max(1, right - left),
      height: Math.max(1, bottom - top),
      bottom
    }

    setSpotlightRect((previous) => {
      if (
        previous &&
        previous.left === nextRect.left &&
        previous.top === nextRect.top &&
        previous.width === nextRect.width &&
        previous.height === nextRect.height
      ) {
        return previous
      }
      return nextRect
    })
  }, [step.targetId])

  useEffect(() => {
    if (!isOpen) {
      setSpotlightRect(null)
      return
    }

    updateSpotlight()

    const onViewportChange = () => {
      updateSpotlight()
    }

    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
    const poll = window.setInterval(updateSpotlight, 300)

    return () => {
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('scroll', onViewportChange, true)
      window.clearInterval(poll)
    }
  }, [isOpen, step.targetId, updateSpotlight])

  useEffect(() => {
    if (!isOpen || !tooltipRef.current) {
      return
    }

    const node = tooltipRef.current
    const nextSize = {
      width: node.offsetWidth,
      height: node.offsetHeight
    }

    setTooltipSize((previous) => {
      if (previous.width === nextSize.width && previous.height === nextSize.height) {
        return previous
      }
      return nextSize
    })
  }, [isOpen, stepIndex, spotlightRect])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  const tooltipStyle = useMemo(() => {
    const width = Math.min(TOOLTIP_MAX_WIDTH, viewportSize.width - VIEWPORT_PADDING * 2)
    const fallbackTop = Math.max(
      VIEWPORT_PADDING,
      viewportSize.height - (tooltipSize.height || 210) - VIEWPORT_PADDING
    )
    if (!spotlightRect) {
      return {
        width,
        left: clamp(
          (viewportSize.width - width) / 2,
          VIEWPORT_PADDING,
          viewportSize.width - width - VIEWPORT_PADDING
        ),
        top: fallbackTop
      }
    }

    const estimatedHeight = tooltipSize.height || 210
    const spaceBelow = viewportSize.height - spotlightRect.bottom
    const spaceAbove = spotlightRect.top
    const showBelow = spaceBelow >= estimatedHeight + 20 || spaceBelow >= spaceAbove
    const top = showBelow
      ? clamp(
        spotlightRect.bottom + 12,
        VIEWPORT_PADDING,
        viewportSize.height - estimatedHeight - VIEWPORT_PADDING
      )
      : clamp(
        spotlightRect.top - estimatedHeight - 12,
        VIEWPORT_PADDING,
        viewportSize.height - estimatedHeight - VIEWPORT_PADDING
      )

    const left = clamp(
      spotlightRect.left + spotlightRect.width / 2 - width / 2,
      VIEWPORT_PADDING,
      viewportSize.width - width - VIEWPORT_PADDING
    )

    return {
      width,
      left,
      top
    }
  }, [spotlightRect, tooltipSize.height, viewportSize.height, viewportSize.width])

  if (!isOpen) {
    return null
  }

  return (
    <div
      className="fixed inset-0 z-[80]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
      aria-describedby="tutorial-description"
    >
      {!spotlightRect ? (
        <div className="absolute inset-0 bg-black/50" />
      ) : null}
      {spotlightRect ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed rounded-2xl border border-[color:rgba(46,168,255,0.9)] shadow-[0_0_0_9999px_rgba(3,9,18,0.66)] transition-all duration-300 ease-out"
          style={{
            left: `${spotlightRect.left}px`,
            top: `${spotlightRect.top}px`,
            width: `${spotlightRect.width}px`,
            height: `${spotlightRect.height}px`
          }}
        />
      ) : null}

      <div
        ref={tooltipRef}
        className="absolute"
        style={{
          left: `${tooltipStyle.left}px`,
          top: `${tooltipStyle.top}px`,
          width: `${tooltipStyle.width}px`
        }}
      >
        <Card
          className="room-elevated room-glow rounded-2xl p-4 text-[var(--room-text)]"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--room-muted)]">
            Quick Tutorial {stepIndex + 1}/{TUTORIAL_STEPS.length}
          </p>
          <h2 id="tutorial-title" className="mt-1 text-base font-semibold">
            {step.title}
          </h2>
          <p id="tutorial-description" className="mt-2 text-sm text-[var(--room-muted)]">
            {step.body}
          </p>

          <div className="mt-4 flex items-center gap-1">
            {TUTORIAL_STEPS.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 flex-1 rounded-full ${idx <= stepIndex ? 'bg-[var(--room-primary)]' : 'bg-white/15'}`}
              />
            ))}
          </div>

          <div className="mt-5 flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="room-focus-ring h-10 rounded-xl border border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]"
            >
              Skip
            </Button>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStepIndex((previous) => Math.max(0, previous - 1))}
                disabled={stepIndex === 0}
                className="room-focus-ring h-10 rounded-xl border border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (isLastStep) {
                    onComplete?.()
                    return
                  }
                  setStepIndex((previous) => Math.min(TUTORIAL_STEPS.length - 1, previous + 1))
                }}
                className="room-focus-ring h-10 rounded-xl border border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.22)] text-[var(--room-text)] hover:bg-[color:rgba(46,168,255,0.3)]"
              >
                {isLastStep ? 'Done' : 'Next'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default FirstTimeTutorialSheet
